/**
===================================================
Module: frontend.src.components.ResetPassword.tsx
Description: Password reset component
Owner: Kawada Technosystem Co Ltd
All Rights Reserved
This script is proprietary and confidential.
Unauthorized copying, distribution, or modification of this
file, via any medium, is strictly prohibited.
===================================================
*/

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { apiService } from "../services/api";
import { PasswordResetConfirm } from "../types";
import NotificationSystem, { Notification } from "./NotificationSystem";
import "../styles/login.css";

const ResetPassword: React.FC = () => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [token, setToken] = useState<string>("");
  const navigate = useNavigate();
  const location = useLocation();

  // Add notification function
  const addNotification = (
    message: string,
    type: "success" | "error" | "info",
    duration?: number,
  ) => {
    const id = Date.now().toString();
    const notification: Notification = {
      id,
      message,
      type,
      duration: duration || 5000,
    };
    setNotifications((prev) => [...prev, notification]);
  };

  // Remove notification function
  const removeNotification = (id: string) => {
    setNotifications((prev) =>
      prev.filter((notification) => notification.id !== id),
    );
  };

  // Extract token from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tokenParam = urlParams.get("token");

    if (!tokenParam) {
      addNotification(
        "パスワードリセットのリンクが無効です。もう一度リンクを送信してください",
        "error",
      );
      setTimeout(() => navigate("/login"), 3000);
      return;
    }

    setToken(tokenParam);
  }, [location, navigate]);

  const validateForm = (): boolean => {
    if (!newPassword.trim()) {
      addNotification("New password is required.", "error");
      return false;
    }

    if (newPassword.length < 8) {
      addNotification("Password must be at least 8 characters long.", "error");
      return false;
    }

    if (!confirmPassword.trim()) {
      addNotification("Please confirm your new password.", "error");
      return false;
    }

    if (newPassword !== confirmPassword) {
      addNotification(
        "パスワードが一致していません。確認してもう一度お試しください",
        "error",
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const resetData: PasswordResetConfirm = {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      };

      const response = await apiService.resetPassword(resetData);

      addNotification(response.message, "success", 8000);

      // Clear form
      setNewPassword("");
      setConfirmPassword("");

      // Redirect to login after success
      setTimeout(() => {
        navigate("/login?message=password_reset_success");
      }, 3000);
    } catch (error: any) {
      console.error("Password reset error:", error);
      addNotification(
        error.message || "パスワードのリセットに失敗しました。時間をおいてもう一度お試しください",
        "error",
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate("/login");
  };

  return (
    <>
      <NotificationSystem
        notifications={notifications}
        onRemove={removeNotification}
      />

      <div className="login-container">
        <div
          className="left-panel"
          style={{
            background: `
              linear-gradient(135deg, rgba(44, 62, 80, 0.3) 0%, rgba(52, 58, 64, 0.4) 100%),
              url('/cover.png')
            `,
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        ></div>

        <div className="right-panel">
          <form className="login-form" onSubmit={handleSubmit}>
            <div className="form-header">
              <h2>パスワードのリセット</h2>
              <p>以下に新しいパスワードを入力してください</p>
            </div>

            <div className="form-content">
              <div className="form-group">
                <label htmlFor="newPassword">新しいパスワード</label>
                <input
                  type="password"
                  id="newPassword"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="新しいパスワードを入力してください（8文字以上）"
                  minLength={8}
                />
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword">新しいパスワードの確認</label>
                <input
                  type="password"
                  id="confirmPassword"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                  placeholder="新しいパスワードをもう一度入力してください"
                  minLength={8}
                />
              </div>
            </div>

            <button type="submit" className="login-button" disabled={isLoading}>
              <div className="button-content">
                {isLoading && <div className="spinner"></div>}
                <span>
                  {isLoading ? "パスワードのリセット中..." : "パスワードのリセット"}
                </span>
              </div>
            </button>

            <div className="form-toggle">
              <button
                type="button"
                className="toggle-link"
                onClick={handleBackToLogin}
                disabled={isLoading}
                >
                ログイン画面に戻る  
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;
